import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import dayjs from "dayjs";
import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";
import { FormControl, Icon, IconButton } from "@/comn/components";
import { TFormSchema, useForm, useModal } from "@/comn/hooks";
import { IntfFailList } from "@/comn/comn/intf/IntfFailLst";
import { CHART_COLOR, DashboardCard, TabStrip, toNumber } from "./TanogaPortalMain";

/* ------------------------------------------------------------------
 * 인터페이스 실적 통계 — 시스템 운영자(auth_osci0003) 전용 메인 화면
 * ------------------------------------------------------------------
 * 결함관리대장 44번. 연계 이력(OSCI_INTF_TRSC_H)을 송신/수신으로 나누고
 * 방향별 성공·실패를 기관 단위로 집계해 보여 준다.
 *
 * 집계 규칙(분석 설계 부서 확정):
 *  - 성공 = 전송상태 S001, 실패 = E 로 시작하는 상태
 *  - 진행중(R003·C000·R00x 등)은 통계에서 제외하므로 Total = 성공 + 실패
 *  - 기관은 "OSC 의 상대 기관". 송신이면 수신기관, 수신이면 송신기관이 기준
 *  - 엔드포인트 미등록 기관은 Other, 기관코드가 없는 수신 이력은 Unknown 으로 묶는다
 * 위 판정과 기관 매핑은 모두 서버 쿼리가 처리한다. 화면은 받은 건수만 그린다.
 * ------------------------------------------------------------------ */

/** 서버 API 경로. 메인 화면 통계이므로 OsciMainController 계열에 둔다. */
const INTF_STSTCL_API = {
    summary: "/api/v1/osci/comn/main/ststcl/intf-summary",
    byOrg: "/api/v1/osci/comn/main/ststcl/intf-org",
};

/** 송수신 구분. 실패 목록 팝업에 넘기면 팝업이 서버에 그대로 넘긴다. */
const SNDRCV_DVN = { RCVE: "R", SEND: "S" };

/**
 * 레이아웃만 확인할 때 true 로 두면 서버 호출 없이 샘플로 그린다.
 * (기존 대시보드의 DASHBOARD_USE_SAMPLE 과 같은 용도)
 */
const INTF_USE_SAMPLE = false;

/**
 * 서버가 내려주는 묶음 행 키.
 * 고정 문자열 대신 키로 받아 화면에서 라벨로 바꾼다(언어 전환 대응).
 */
const ORG_KEY = { OTHER: "__OTHER__", UNKNOWN: "__UNKNOWN__" };

/**
 * 성공률 경고 기준(%).
 * 상태 배지·행 강조·성공률 색·실패 건수 강조가 모두 이 값 하나를 본다.
 * 무배포 조정이 필요해지면 서버 설정으로 옮기고 판정도 서버로 함께 옮긴다.
 */
const RATE_THRESHOLD = 95;

/* 색은 대시보드 팔레트를 그대로 쓴다. 새 색을 만들면 테마별로 또 관리해야 한다. */
const COLOR_RCVE = CHART_COLOR[0];
const COLOR_SEND = CHART_COLOR[2];
const COLOR_GOOD = CHART_COLOR[2];
const COLOR_ALERT = CHART_COLOR[4];
const COLOR_WARN = CHART_COLOR[1];
const COLOR_MUTED = CHART_COLOR[11];

/** 기간 프리셋. 서버에는 항상 환산된 일자 범위만 보낸다. */
const PERIOD_ITEMS = [
    { key: "D", labelKey: "L_TODAY" },
    { key: "W", labelKey: "L_THIS_WEEK" },
    { key: "M", labelKey: "L_THIS_MONTH" },
    { key: "Y", labelKey: "L_THIS_YEAR" },
    { key: "C", labelKey: "L_CUSTOM" },
];

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * 기간 조회 조건.
 * 달력은 공통 daterange 컨트롤을 쓴다. 프리셋을 고르면 값만 채우고 입력을 막고,
 * Custom 일 때만 열어 사용자가 달력에서 직접 고르게 한다.
 */
const SF_INTF_STSTC_SRCH: TFormSchema = {
    id: "form_intfStstclSrch",
    schema: {
        srchPrid: {
            type: "daterange",
            label: "L_PRID",
            start: { name: "srchStrtDt" },
            end: { name: "srchEndDt" },
        },
    },
};

/** 프리셋을 시작·종료 일자로 환산한다. Custom 은 사용자가 고르므로 만들지 않는다. */
const periodToRange = (key: string) => {
    const today = dayjs();
    const endDt = today.format(DATE_FORMAT);
    switch (key) {
        case "D":
            return { srchStrtDt: endDt, srchEndDt: endDt };
        case "W":
            return { srchStrtDt: today.startOf("week").format(DATE_FORMAT), srchEndDt: endDt };
        case "Y":
            return { srchStrtDt: today.startOf("year").format(DATE_FORMAT), srchEndDt: endDt };
        case "M":
        default:
            return { srchStrtDt: today.startOf("month").format(DATE_FORMAT), srchEndDt: endDt };
    }
};

/**
 * 달력 값을 YYYY-MM-DD 문자열로 맞춘다.
 * 공통 daterange 컨트롤은 값을 Date 객체로 보관하므로 그대로 서버에 보내면
 * "Tue Sep 01 2026 00:00:00 GMT+0900 ..." 이 나가 TO_DATE 가 실패한다.
 */
const toDateText = (value: any) => (value ? dayjs(value).format(DATE_FORMAT) : "");

/** 성공률(%). 집계 건이 없으면 비율을 낼 수 없으므로 null. */
const successRate = (sucsCnt: any, totCnt: any) => {
    const total = Number(totCnt ?? 0);
    if (total <= 0) return null;
    return (Number(sucsCnt ?? 0) / total) * 100;
};

const formatRate = (rate: number | null) => (rate === null ? "—" : `${rate.toFixed(1)}%`);

/** 기준 미달 여부. 집계 건이 없는 방향은 미달로 보지 않는다. */
const isLowRate = (rate: number | null) => rate !== null && rate < RATE_THRESHOLD;

const STATUS = { HEALTHY: "H", ATTENTION: "A", NO_DATA: "N" };

/** 기관 표시명. 묶음 행은 라벨로, 나머지는 약어(없으면 코드)로 보여 준다. */
const orgLabel = (row: any, t: any) => {
    if (row.orgCd === ORG_KEY.OTHER) return t("L_OTHER");
    if (row.orgCd === ORG_KEY.UNKNOWN) return t("L_UNKNOWN");
    return row.orgAbrtNm || row.orgCd;
};

/**
 * 기관 한 행의 상태.
 * 집계 건이 0인 방향은 성공률을 낼 수 없으므로 판정에서 뺀다(수신만 있는 Unknown 행이 여기 해당).
 * 판정할 방향이 하나도 없으면 기간 내 연계가 없었다는 뜻이라 No data 로 둔다.
 * 이때 Healthy 로 두면 "잘 되고 있다"로 읽혀 오해를 만든다.
 */
const rowStatus = (row: any) => {
    const rates = [successRate(row.rcveSucsCnt, row.rcveTotCnt), successRate(row.sendSucsCnt, row.sendTotCnt)].filter(
        (rate): rate is number => rate !== null,
    );

    if (!rates.length) return STATUS.NO_DATA;
    return rates.every((rate) => rate >= RATE_THRESHOLD) ? STATUS.HEALTHY : STATUS.ATTENTION;
};

/** 설계 시안의 예시 수치. 실데이터가 아니다. */
const SAMPLE_ORG = [
    ["OGAM0033", "TRA", 612, 598, 14, 587, 576, 11],
    ["OGAZ0001", "ZBS", 318, 313, 5, 302, 297, 5],
    ["OGAZ0009", "ZURA", 204, 201, 3, 196, 193, 3],
    ["OGAZ0010", "ZFDA", 226, 223, 3, 218, 214, 4],
    ["OGAZ0011", "ZMA", 141, 139, 2, 137, 134, 3],
    ["OGAZ0012", "CGCLA", 173, 167, 6, 166, 163, 3],
    ["OGAZ0015", "MOBEF", 118, 102, 16, 112, 109, 3],
    ["OGAZ0028", "ZAWEMA", 96, 95, 1, 92, 91, 1],
    ["OGAZ0043", "ZanMalipo", 187, 184, 3, 237, 211, 26],
    ["OGAZ0044", "SHIPCO", 132, 130, 2, 128, 125, 3],
    ["OGAZ0046", "NEW", 12, 12, 0, 9, 9, 0],
    ["__OTHER__", "Other", 41, 40, 1, 163, 149, 14],
    ["__UNKNOWN__", "Unknown", 153, 150, 3, 0, 0, 0],
].map(([orgCd, orgAbrtNm, rcveTotCnt, rcveSucsCnt, rcveFailCnt, sendTotCnt, sendSucsCnt, sendFailCnt]) => ({
    orgCd,
    orgAbrtNm,
    rcveTotCnt,
    rcveSucsCnt,
    rcveFailCnt,
    sendTotCnt,
    sendSucsCnt,
    sendFailCnt,
}));

const SAMPLE_SUMMARY = {
    rcveTotCnt: 2413,
    rcveSucsCnt: 2354,
    rcveFailCnt: 59,
    sendTotCnt: 2347,
    sendSucsCnt: 2271,
    sendFailCnt: 76,
};

const EMPTY_SUMMARY = {
    rcveTotCnt: 0,
    rcveSucsCnt: 0,
    rcveFailCnt: 0,
    sendTotCnt: 0,
    sendSucsCnt: 0,
    sendFailCnt: 0,
};

/**
 * 성공률 원형 게이지.
 * ECharts 를 쓰기엔 과한 표현이라 SVG 원 하나를 stroke-dasharray 로 채운다.
 */
const RateGauge = ({ rate, color }: any) => {
    const { t } = useTranslation();
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const filled = rate === null ? 0 : (circumference * Math.max(0, Math.min(100, rate))) / 100;

    return (
        <div className="relative w-[120px] h-[120px] shrink-0">
            <svg viewBox="0 0 96 96" className="w-[120px] h-[120px] -rotate-90">
                <circle
                    cx="48"
                    cy="48"
                    r={radius}
                    fill="none"
                    strokeWidth="8"
                    stroke={COLOR_MUTED}
                    strokeOpacity={0.25}
                />
                <circle
                    cx="48"
                    cy="48"
                    r={radius}
                    fill="none"
                    strokeWidth="8"
                    strokeLinecap="round"
                    stroke={color}
                    strokeDasharray={`${filled} ${circumference - filled}`}
                />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <b className="text-xl font-bold leading-none">{formatRate(rate)}</b>
                <span className="text-xs text-darkgray uppercase mt-[4px]">{t("L_SUCS")}</span>
            </div>
        </div>
    );
};

/** 방향별 전체 KPI 카드. accentColor 로 방향(수신/송신)을 구분한다. */
const DirectionCard = ({ title, desc, caption, icon, accentColor, totCnt, sucsCnt, failCnt }: any) => {
    const { t } = useTranslation();
    const rate = successRate(sucsCnt, totCnt);

    return (
        <div className="flex-grow-[1] w-full bg-card rounded-[5px] overflow-hidden shadow-md flex flex-col border-[1px]">
            <div className="h-[3px] w-full" style={{ backgroundColor: accentColor }} />
            <div className="p-[20px] flex flex-col">
                <div className="flex items-center justify-between gap-[12px]">
                    <span
                        className="flex items-center gap-[8px] text-sm font-bold uppercase tracking-wider"
                        style={{ color: accentColor }}
                    >
                        <Icon icon={icon} size="sm" />
                        {title}
                    </span>
                    <span className="text-sm text-darkgray">{desc}</span>
                </div>

                <div className="flex items-center justify-between gap-[20px] mt-[14px]">
                    <div>
                        <div className="text-[44px] font-bold leading-none tracking-tight">{toNumber(totCnt)}</div>
                        <div className="text-sm text-darkgray mt-[10px]">{caption}</div>
                    </div>
                    <RateGauge rate={rate} color={accentColor} />
                </div>

                <div className="grid grid-cols-2 gap-[12px] mt-[16px]">
                    <div className="flex items-center justify-between px-[16px] py-[12px] rounded-[6px] bg-header border-[1px] border-card-border">
                        <span className="flex items-center gap-[8px] text-sm text-darkgray font-semibold">
                            <i
                                className="inline-block w-[10px] h-[10px] rounded-full"
                                style={{ backgroundColor: COLOR_GOOD }}
                            />
                            {t("L_SUCS")}
                        </span>
                        <span className="text-xl font-bold" style={{ color: COLOR_GOOD }}>
                            {toNumber(sucsCnt)}
                        </span>
                    </div>
                    <div className="flex items-center justify-between px-[16px] py-[12px] rounded-[6px] bg-header border-[1px] border-card-border">
                        <span className="flex items-center gap-[8px] text-sm text-darkgray font-semibold">
                            <i
                                className="inline-block w-[10px] h-[10px] rounded-full"
                                style={{ backgroundColor: COLOR_ALERT }}
                            />
                            {t("L_FAIL")}
                        </span>
                        <span className="text-xl font-bold" style={{ color: COLOR_ALERT }}>
                            {toNumber(failCnt)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * 상태 배지. Healthy / Attention / No data.
 * compact 는 카드 머리의 범례처럼 작은 글씨 옆에 둘 때 쓴다. 색·아이콘은 표와 같은 것을 쓴다.
 */
const StatusBadge = ({ status, compact = false }: any) => {
    const { t } = useTranslation();
    const view: any = {
        [STATUS.HEALTHY]: { icon: "check", color: COLOR_GOOD, labelKey: "L_HEALTHY" },
        [STATUS.ATTENTION]: { icon: "exclamationTriangle", color: COLOR_WARN, labelKey: "L_ATTENTION" },
        [STATUS.NO_DATA]: { icon: "minus", color: COLOR_MUTED, labelKey: "L_NO_DATA" },
    }[status];

    return (
        <span
            className={classNames(
                "inline-flex items-center rounded-[6px] border-[1px] font-bold whitespace-nowrap",
                compact ? "gap-[4px] px-[6px] py-[1px] text-xs" : "gap-[6px] px-[10px] py-[4px] text-sm",
            )}
            style={{ color: view.color, borderColor: view.color, backgroundColor: `${view.color}22` }}
        >
            <Icon icon={view.icon} size="xs" />
            {t(view.labelKey)}
        </span>
    );
};

/**
 * 상태 범례. 기관별 상세 표의 Status 열이 어떤 기준으로 갈리는지 카드 머리에 적어 둔다.
 *  - Healthy   : 건이 있는 방향이 모두 기준 이상
 *  - Attention : 한 방향이라도 기준 미만
 *  - No data   : 기간 내 연계 0건
 * 기준값은 RATE_THRESHOLD 를 그대로 읽는다. 기준을 바꾸면 범례도 함께 바뀌어야 하므로 숫자를 적지 않는다.
 * 좁은 화면에서는 항목 단위로 줄을 바꾼다(항목 안에서는 끊지 않는다).
 */
const StatusLegend = () => {
    const { t } = useTranslation();
    const items = [
        { status: STATUS.HEALTHY, text: `${t("L_SUCS_RATE")} ≥ ${RATE_THRESHOLD}%` },
        { status: STATUS.ATTENTION, text: `${t("L_SUCS_RATE")} < ${RATE_THRESHOLD}%` },
        { status: STATUS.NO_DATA, text: `${t("L_TOT_CNT")} 0` },
    ];

    return (
        <span className="inline-flex flex-wrap items-center justify-end gap-x-[14px] gap-y-[4px] ml-[12px]">
            {items.map((item) => (
                <span key={item.status} className="inline-flex items-center gap-[6px] whitespace-nowrap">
                    <StatusBadge status={item.status} compact={true} />
                    {item.text}
                </span>
            ))}
        </span>
    );
};

/**
 * 방향 한 쌍(Total/Success/Failure/Rate) 셀.
 * 집계 건이 0인 방향은 성공률을 낼 수 없으므로 네 칸 모두 — 로 둔다.
 * 실제로 0건 실패인 기관(총 건수는 있는)은 숫자가 그대로 남는다.
 *
 * onFailClick 을 주면 실패 건수가 목록 팝업으로 넘어가는 링크가 된다.
 * 합계 행도 넘긴다. 합계 행은 전 기관 목록을 연다(openFailListAll).
 */
const DirectionCells = ({ totCnt, sucsCnt, failCnt, onFailClick }: any) => {
    const rate = successRate(sucsCnt, totCnt);
    const low = isLowRate(rate);
    const cell = "px-[12px] py-[11px] text-right";
    /* 0건이면 열어 볼 목록이 없다. 링크 표시도 하지 않는다. */
    const failLink = Boolean(onFailClick) && Number(failCnt ?? 0) > 0;

    if (rate === null) {
        return (
            <>
                <td className={classNames(cell, "border-l-[1px] border-card-border text-darkgray")}>—</td>
                <td className={classNames(cell, "text-darkgray")}>—</td>
                <td className={classNames(cell, "text-darkgray")}>—</td>
                <td className={classNames(cell, "text-darkgray")}>—</td>
            </>
        );
    }

    /* 기준 미달인 실패 건수는 배지로 강조한다. */
    const failText = low ? (
        <span
            className="inline-block min-w-[26px] px-[7px] rounded-full font-bold"
            style={{ color: COLOR_ALERT, backgroundColor: `${COLOR_ALERT}22` }}
        >
            {toNumber(failCnt)}
        </span>
    ) : (
        toNumber(failCnt)
    );

    /* 위 표기를 그대로 두고 링크만 얹는다. 배지는 이미 경고색이라 링크 색은 그 외의 경우에만 준다. */
    const failLinked = (
        <a
            onClick={(e) => {
                e.preventDefault();
                onFailClick();
            }}
            className="underline"
            style={{ cursor: "pointer", color: low ? undefined : COLOR_ALERT }}
        >
            {failText}
        </a>
    );

    return (
        <>
            <td className={classNames(cell, "border-l-[1px] border-card-border")}>{toNumber(totCnt)}</td>
            <td className={cell}>{toNumber(sucsCnt)}</td>
            <td className={cell}>{failLink ? failLinked : failText}</td>
            {/* 성공률은 숫자 아래 얇은 막대를 함께 둬서 행 간 비교가 눈으로 되게 한다. */}
            <td
                className={classNames(cell, "relative w-[104px] font-bold")}
                style={low ? { color: COLOR_ALERT } : undefined}
            >
                {formatRate(rate)}
                <span
                    className="absolute left-[12px] right-[12px] bottom-[6px] h-[3px] rounded-[2px]"
                    style={{ backgroundColor: `${COLOR_MUTED}33` }}
                >
                    <span
                        className="absolute left-0 top-0 bottom-0 rounded-[2px]"
                        style={{ width: `${rate}%`, backgroundColor: low ? COLOR_ALERT : COLOR_GOOD }}
                    />
                </span>
            </td>
        </>
    );
};

/** 기관별 상세 테이블. 합계 행은 목록을 합산해 만든다. */
const OrgDetailCard = ({ orgList, strtDt, endDt }: any) => {
    const { t } = useTranslation();
    const modal = useModal();

    /**
     * 실패 목록 팝업을 연다.
     * 기관 키는 서버가 내려준 행의 값(orgCd)을 그대로 넘긴다.
     * 묶음 행은 __OTHER__ / __UNKNOWN__ 이 키이므로 별도로 손대지 않는다.
     * 팝업 컴포넌트를 content 로 직접 띄운다. url(iframe) 방식은 높이가 90vh 로 고정돼
     * 목록보다 커지므로 쓰지 않는다. 제목은 팝업 컴포넌트가 Page title 로 직접 그린다.
     * 기관명은 언어 전환이 반영된 표시명을 그대로 넘겨 팝업이 다시 매핑하지 않게 한다.
     */
    const openFailPopup = (orgKey: string, orgNm: string, sndrcvDvn: string) => {
        modal.openModal({
            content: <IntfFailList params={{ orgKey, orgNm, sndrcvDvn, strtDt, endDt }} />,
            size: "xl",
            draggable: true,
        });
    };

    const openFailList = (row: any, sndrcvDvn: string) => openFailPopup(row.orgCd, orgLabel(row, t), sndrcvDvn);

    /**
     * 합계 행의 실패 건수는 같은 방향·기간의 전 기관 목록을 연다.
     * 기관 키를 빈 값으로 넘기면 서버가 기관 조건을 빼고 조회한다.
     * 판정 기준(intfStstclBase)이 같으므로 목록 전체 건수가 합계 행의 실패 건수와 같다.
     * 기관명 칸에는 기존 라벨 L_AL(All)을 쓴다.
     */
    const openFailListAll = (sndrcvDvn: string) => openFailPopup("", t("L_AL"), sndrcvDvn);

    /* 합계는 KPI(요약 API) 값을 가져다 쓰지 않고 목록을 합산한다.
       두 값이 어긋나면 쿼리 조건이 다르다는 뜻이므로 화면에서 드러나는 편이 낫다. */
    const totals = useMemo(
        () =>
            orgList.reduce(
                (acc: any, row: any) => ({
                    rcveTotCnt: acc.rcveTotCnt + Number(row.rcveTotCnt ?? 0),
                    rcveSucsCnt: acc.rcveSucsCnt + Number(row.rcveSucsCnt ?? 0),
                    rcveFailCnt: acc.rcveFailCnt + Number(row.rcveFailCnt ?? 0),
                    sendTotCnt: acc.sendTotCnt + Number(row.sendTotCnt ?? 0),
                    sendSucsCnt: acc.sendSucsCnt + Number(row.sendSucsCnt ?? 0),
                    sendFailCnt: acc.sendFailCnt + Number(row.sendFailCnt ?? 0),
                }),
                { ...EMPTY_SUMMARY },
            ),
        [orgList],
    );

    /* 머리칸은 모두 가운데 정렬. 본문·합계 칸 정렬(숫자 오른쪽)은 그대로 둔다. */
    const headCell = "px-[12px] py-[10px] text-center whitespace-nowrap bg-header sticky top-0";

    return (
        <DashboardCard title={t("T_DTL_BY_ORG")} extra={<StatusLegend />} bodyClassName="p-0">
            <div className="w-full overflow-x-auto">
                <table className="w-full border-collapse text-[15px]">
                    <thead className="text-darkgray text-xs tracking-wide">
                        <tr>
                            <th rowSpan={2} className={classNames(headCell, "text-center align-bottom")}>
                                {t("L_ORG")}
                            </th>
                            <th
                                colSpan={4}
                                className={classNames(headCell, "text-center border-l-[1px] border-card-border")}
                                style={{ color: COLOR_RCVE }}
                            >
                                {t("L_RCVE")}
                            </th>
                            <th
                                colSpan={4}
                                className={classNames(headCell, "text-center border-l-[1px] border-card-border")}
                                style={{ color: COLOR_SEND }}
                            >
                                {t("L_SEND")}
                            </th>
                            <th
                                rowSpan={2}
                                className={classNames(
                                    headCell,
                                    "text-center align-bottom border-l-[1px] border-card-border w-[140px]",
                                )}
                            >
                                {t("L_STAT")}
                            </th>
                        </tr>
                        <tr>
                            {[0, 1].map((dvn) => (
                                <Fragment key={dvn}>
                                    <th className={classNames(headCell, "border-l-[1px] border-card-border")}>
                                        {t("L_TOT_CNT")}
                                    </th>
                                    <th className={headCell}>{t("L_SUCS")}</th>
                                    <th className={headCell}>{t("L_FAIL")}</th>
                                    <th className={headCell}>{t("L_SUCS_RATE")}</th>
                                </Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {orgList.map((row: any) => {
                            /* 한 방향이라도 기준 미만이면 행 왼쪽에 경고 선을 준다. */
                            const warn =
                                isLowRate(successRate(row.rcveSucsCnt, row.rcveTotCnt)) ||
                                isLowRate(successRate(row.sendSucsCnt, row.sendTotCnt));

                            return (
                                <tr
                                    key={row.orgCd}
                                    className="border-b-[1px] border-card-border even:bg-[rgba(127,143,166,0.06)]"
                                >
                                    <td
                                        className="px-[12px] py-[11px] font-bold whitespace-nowrap"
                                        style={warn ? { boxShadow: `inset 3px 0 0 ${COLOR_ALERT}` } : undefined}
                                    >
                                        {orgLabel(row, t)}
                                    </td>
                                    <DirectionCells
                                        totCnt={row.rcveTotCnt}
                                        sucsCnt={row.rcveSucsCnt}
                                        failCnt={row.rcveFailCnt}
                                        onFailClick={() => openFailList(row, SNDRCV_DVN.RCVE)}
                                    />
                                    <DirectionCells
                                        totCnt={row.sendTotCnt}
                                        sucsCnt={row.sendSucsCnt}
                                        failCnt={row.sendFailCnt}
                                        onFailClick={() => openFailList(row, SNDRCV_DVN.SEND)}
                                    />
                                    <td className="px-[12px] py-[11px] text-center border-l-[1px] border-card-border">
                                        <StatusBadge status={rowStatus(row)} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr className="bg-header font-bold border-t-[2px] border-card-border">
                            <td className="px-[12px] py-[11px] whitespace-nowrap">
                                {t("L_TOT_CNT")} ({orgList.length})
                            </td>
                            <DirectionCells
                                totCnt={totals.rcveTotCnt}
                                sucsCnt={totals.rcveSucsCnt}
                                failCnt={totals.rcveFailCnt}
                                onFailClick={() => openFailListAll(SNDRCV_DVN.RCVE)}
                            />
                            <DirectionCells
                                totCnt={totals.sendTotCnt}
                                sucsCnt={totals.sendSucsCnt}
                                failCnt={totals.sendFailCnt}
                                onFailClick={() => openFailListAll(SNDRCV_DVN.SEND)}
                            />
                            {/* 합계 행에는 상태 배지를 두지 않는다. 기관 단위 판정이라 합쳐서는 의미가 없다. */}
                            <td className="px-[12px] py-[11px] border-l-[1px] border-card-border" />
                        </tr>
                    </tfoot>
                </table>
            </div>
        </DashboardCard>
    );
};

/**
 * 인터페이스 실적 통계 화면 본체.
 * 시스템 운영자만 들어오는 화면이라 기관 조건을 따로 두지 않는다(서버가 전 기관으로 집계).
 */
const TanogaIntfStstcl = () => {
    const { t } = useTranslation();
    /* 기본 조회 기간은 이번 주(W). 탭 선택값과 달력 초기값이 같은 키를 봐야 표시와 조회 구간이 어긋나지 않는다. */
    const [period, setPeriod] = useState("W");
    const [summary, setSummary] = useState<any>(INTF_USE_SAMPLE ? SAMPLE_SUMMARY : EMPTY_SUMMARY);
    const [orgList, setOrgList] = useState<any[]>(INTF_USE_SAMPLE ? SAMPLE_ORG : []);

    const form = useForm({ defaultSchema: SF_INTF_STSTC_SRCH, defaultValues: periodToRange("W") });

    /* 달력 컨트롤은 값을 Date 객체로 들고 있다. 그대로 보내면 서버의 TO_DATE 가 깨지므로
       화면에서 쓰기 전에 YYYY-MM-DD 문자열로 맞춘다. */
    const strtDt = toDateText(form.watch("srchStrtDt"));
    const endDt = toDateText(form.watch("srchEndDt"));

    const periodItems = useMemo(() => PERIOD_ITEMS.map((item) => ({ key: item.key, label: t(item.labelKey) })), [t]);

    /**
     * 프리셋을 고르면 즉시 일자 범위로 환산해 폼에 넣는다.
     * Custom 은 지금 찍혀 있는 범위를 그대로 두고 사용자가 달력에서 고치게 둔다.
     */
    const onPeriodChange = (key: string) => {
        setPeriod(key);
        if (key !== "C") form.setValues(periodToRange(key));
    };

    const load = useCallback(async () => {
        if (INTF_USE_SAMPLE) return;
        /* 한쪽만 고른 상태나 뒤집힌 범위로는 질의하지 않는다. */
        if (!strtDt || !endDt || dayjs(endDt).isBefore(dayjs(strtDt))) return;

        const base = process.env.REACT_APP_API_OSCI || "";
        const params = comnUtils.toGetParams({ srchStrtDt: strtDt, srchEndDt: endDt });

        try {
            const [summRes, orgRes] = await Promise.all([
                api.get(base + INTF_STSTCL_API.summary, { params }),
                api.get(base + INTF_STSTCL_API.byOrg, { params }),
            ]);
            setSummary(summRes.data?.intfStstclSumm?.content ?? EMPTY_SUMMARY);
            setOrgList(orgRes.data?.intfStstclOrgList?.content ?? []);
        } catch (error) {
            /* 조회에 실패하면 이전 수치를 남겨 두지 않는다. 오래된 값이 현재 실적으로 읽히면 안 된다. */
            console.log(error);
            setSummary(EMPTY_SUMMARY);
            setOrgList([]);
        }
    }, [strtDt, endDt]);

    /**
     * 프리셋은 구간이 계산되는 즉시 조회한다.
     * Custom 은 사용자가 두 날짜를 다 고른 뒤 돋보기를 눌러야 조회한다.
     * 자동으로 걸면 시작일만 바꾼 중간 상태로도 질의가 나간다.
     */
    useEffect(() => {
        if (period === "C") return;
        load();
    }, [load, period]);

    const caption = `${strtDt} → ${endDt}`;

    return (
        <div className="dashboard-wrapper h-auto lg:min-h-[calc(100vh-145px)] pb-[20px] w-full">
            <div className="flex flex-col gap-[20px]">
                {/* 기간 필터 */}
                <div className="flex flex-wrap items-center justify-between gap-[10px]">
                    <h2 className="text-lg font-bold">{t("T_INTF_TRMSSN_STSTC")}</h2>
                    <div className="flex flex-wrap items-center gap-[10px]">
                        <TabStrip items={periodItems} active={period} onChange={onPeriodChange} bare={true} />
                        {/*
                         * 어느 프리셋에서도 같은 달력 컨트롤을 보여 주되, 프리셋 구간은
                         * 계산된 값이므로 손대지 못하게 막는다. Custom 일 때만 입력을 연다.
                         * size 기본값(full)이면 폭이 행 전체로 늘어나므로 내용 폭에 맞춘다.
                         */}
                        <FormControl {...form.schema.srchPrid} label={undefined} size="fit" disabled={period !== "C"} />
                        {/*
                         * Custom 전용 조회 트리거. 프리셋은 누르는 즉시 조회되므로 이 버튼이 필요 없다.
                         * Custom 은 시작일·종료일을 두 번에 걸쳐 고르기 때문에 자동 조회를 걸 수 없다.
                         */}
                        <IconButton icon="search" disabled={period !== "C"} onClick={() => load()}>
                            {t("B_SRCH")}
                        </IconButton>
                    </div>
                </div>

                {/* 방향별 전체 KPI */}
                <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-[10px]">
                    <DirectionCard
                        title={t("L_RCVE")}
                        desc={t("L_INBND_MSG")}
                        caption={caption}
                        icon="down"
                        accentColor={COLOR_RCVE}
                        totCnt={summary.rcveTotCnt}
                        sucsCnt={summary.rcveSucsCnt}
                        failCnt={summary.rcveFailCnt}
                    />
                    <DirectionCard
                        title={t("L_SEND")}
                        desc={t("L_OUTBND_MSG")}
                        caption={caption}
                        icon="up"
                        accentColor={COLOR_SEND}
                        totCnt={summary.sendTotCnt}
                        sucsCnt={summary.sendSucsCnt}
                        failCnt={summary.sendFailCnt}
                    />
                </div>

                {/* 기관별 상세 — 전체 폭. 실패 목록 팝업이 같은 기간으로 조회하도록 구간을 함께 넘긴다. */}
                <OrgDetailCard orgList={orgList} strtDt={strtDt} endDt={endDt} />
            </div>
        </div>
    );
};

export default TanogaIntfStstcl;
